import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from '@reach/router';
import axiosUI from '../../axiosUI';
import dotProp from 'dot-prop-immutable';
import Sidebar from '../Sidebar/Sidebar';
import Topbar from '../Topbar/Topbar';
import Main from '../Main/Main';
import Loader from '../Loader/Loader';
import PopupWrapper from '../PopupWrapper';
import Button from '../Button/Button';
import { withGlobalContext } from '../../App';
import withCommonUtils from '../withCommonUtils';
import {
  SALESCHANNEL_NAME_MAP,
  NOTIFICATIONS,
  LOCALE_NAME_MAP,
  RESOURCE_STATES,
  OPERATION_TYPES,
  WEEKDAY_ORDER,
} from '../../constants';
import OpeningHourCard from './OpeningHourCard';

const range = (start, stop, step = 1) =>
  Array(Math.ceil((stop - start) / step))
    .fill(start)
    .map((x, y) => x + y * step);

class OpeningHoursPerMarket extends Component {
  static propTypes = {
    country: PropTypes.string.isRequired,
    language: PropTypes.string.isRequired,
    navigate: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      templates: [],
      hasDrafts: false,
      showUnsavedChangesWarningPopup: false,
      showDeleteWarningPopup: false,
      loading: false,
    };

    this.hourRange = range(0, 24).map(hour => ({
      label: String(hour).padStart(2, 0),
      value: String(hour).padStart(2, 0),
    }));
    this.minutesRange = range(0, 60, 5).map(min => ({
      label: String(min).padStart(2, 0),
      value: String(min).padStart(2, 0),
    }));
  }

  onAddDay = (templateIndex, timeRowIndex) => {
    const template = this.state.templates[templateIndex];
    const day = template.opening_hour_times[timeRowIndex].weekday;
    const newRow = this.initializeTimeRow(day, template.id);

    const openingHoursTimes = template.opening_hour_times.slice();
    openingHoursTimes.splice(timeRowIndex + 1, 0, newRow);

    this.setState({
      templates: dotProp.set(
        this.state.templates,
        `${templateIndex}.opening_hour_times`,
        openingHoursTimes
      ),
    });
  };

  onRemoveDay = (templateIndex, timeRowIndex) => {
    this.setState({
      templates: dotProp.delete(
        this.state.templates,
        `${templateIndex}.opening_hour_times.${timeRowIndex}`
      ),
    });
  };

  initializeTimeRow = (day, templateId) => {
    return {
      weekday: day,
      id: uuid(),
      openingHour: '00',
      openingMinute: '00',
      closingHour: '00',
      closingMinute: '00',
      wholeDay: false,
      dayOff: false,
      opening_hour_id: templateId,
    };
  };

  toggleUnsavedChangesWarningPopup = () => {
    this.setState(prevState => ({
      showUnsavedChangesWarningPopup: !prevState.showUnsavedChangesWarningPopup,
    }));
  };

  toggleDeleteWarningPopup = configIndex => {
    this.setState(prevState => {
      return {
        showDeleteWarningPopup:
          prevState.showDeleteWarningPopup === false ? configIndex : false,
      };
    });
  };

  componentDidMount = async () => {
    this.fetchTemplatesAndSetState();
  };

  fetchTemplatesAndSetState = async () => {
    this.setState({ loading: true }, async () => {
      try {
        let {
          data: { templates, hasDrafts },
        } = await axiosUI.get(`/api/opening-hours`, {
          params: {
            salesChannel: this.props.country,
            locale: this.props.language,
          },
        });
        const transformedTemplates = templates.map(template => {
          template.opening_hour_times = template.opening_hour_times.map(
            timeConfig => {
              return {
                ...timeConfig,
                ...this.getSplittedTimes(timeConfig),
              };
            }
          );

          // sort weekdays
          template.opening_hour_times.sort((time1, time2) => {
            return (
              WEEKDAY_ORDER.indexOf(time1.weekday) -
              WEEKDAY_ORDER.indexOf(time2.weekday)
            );
          });

          return template;
        });

        this.setState({ templates: transformedTemplates, hasDrafts });
      } catch (err) {
        console.log(err);
        this.props.createNotification({
          message: `Failed to fetch opening hours`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }

      this.setState({ loading: false });
    });
  };

  getSplittedTimes = timeConfig => {
    const [openingHour, openingMinute] = timeConfig.opening_time.split(':');
    const [closingHour, closingMinute] = timeConfig.closing_time.split(':');
    const wholeDay = timeConfig.whole_day || false;
    const dayOff = timeConfig.day_off || false;
    return {
      openingHour,
      openingMinute,
      closingHour,
      closingMinute,
      wholeDay,
      dayOff,
    };
  };

  addNewTemplate = () => {
    const templateId = uuid();
    const defaultTimeZone = '+01:00';
    const newTemplate = {
      id: templateId,
      name: '',
      type: 'ALL',
      time_zone: defaultTimeZone,
      sales_channel_id: this.props.country,
      locale: this.props.language,
      state: RESOURCE_STATES.UNSAVED,
      operation_type: OPERATION_TYPES.CREATE,
      opening_hour_times: WEEKDAY_ORDER.map(day =>
        this.initializeTimeRow(day, templateId)
      ),
    };

    this.setState({ templates: [newTemplate, ...this.state.templates] });
  };

  onChange = event => {
    const templateIndex = Number(event.target.name.split('.')[0]);
    const currentTemplate = this.state.templates[templateIndex];
    const updatedTemplateList = dotProp.set(
      this.state.templates,
      event.target.name,
      event.target.value
    );
    updatedTemplateList[
      templateIndex
    ].operation_type = this.props.getNewOperationType(currentTemplate);
    updatedTemplateList[templateIndex].state = RESOURCE_STATES.UNSAVED;
    this.setState({
      templates: updatedTemplateList,
    });
  };

  timeRowInputFieldUpdate = event => {
    const {
      target: { name, value, checked, type },
    } = event;
    const fieldValue = type === 'checkbox' ? checked : value;
    const splittedName = name.split('.');
    const index = Number(splittedName[0]);
    const field = splittedName.slice(-1)[0];
    const updatedList = dotProp.set(this.state.templates, name, fieldValue);
    const currentTemplate = updatedList[index];
    const templateType = currentTemplate.type;

    // in case of WEEKDAY_WEEKEND and ALL, multiple field needs to be updated
    // in WEEKDAY_WEEKEND:
    // -> 0th row corresponds to all weekday values
    // -> 5th row corresponds to all weekend values
    // in ALL:
    // -> 0th row corresponds to all days values
    // we're doing this to keep sync with backend payload
    switch (templateType) {
      case 'WEEKDAY_WEEKEND': {
        const isWeekend = Number(splittedName[2]) === 5;
        if (isWeekend) {
          currentTemplate.opening_hour_times[5][field] = fieldValue;
          currentTemplate.opening_hour_times[6][field] = fieldValue;
        } else {
          currentTemplate.opening_hour_times[0][field] = fieldValue;
          currentTemplate.opening_hour_times[1][field] = fieldValue;
          currentTemplate.opening_hour_times[2][field] = fieldValue;
          currentTemplate.opening_hour_times[3][field] = fieldValue;
          currentTemplate.opening_hour_times[4][field] = fieldValue;
        }
        break;
      }
      case 'ALL': {
        currentTemplate.opening_hour_times[0][field] = fieldValue;
        currentTemplate.opening_hour_times[1][field] = fieldValue;
        currentTemplate.opening_hour_times[2][field] = fieldValue;
        currentTemplate.opening_hour_times[3][field] = fieldValue;
        currentTemplate.opening_hour_times[4][field] = fieldValue;
        currentTemplate.opening_hour_times[5][field] = fieldValue;
        currentTemplate.opening_hour_times[6][field] = fieldValue;
        break;
      }
      default:
        break;
    }

    currentTemplate.operation_type = this.props.getNewOperationType(
      currentTemplate
    );
    currentTemplate.state = RESOURCE_STATES.UNSAVED;
    updatedList[index] = currentTemplate;
    this.setState(
      {
        templates: updatedList,
      },
      () => {
        // if you're checking wholeDay to ON, then dayOff should be OFF
        // and vice a versa
        if (field === 'wholeDay' || field === 'dayOff') {
          const currentToggleType = field;
          const currentToggleValue = fieldValue;
          const counterToggle = currentToggleType === 'wholeDay' ? 'dayOff' : 'wholeDay';
          const clonedSplittedName = splittedName.slice();
          clonedSplittedName[3] = counterToggle;
          const counterTogglePath = clonedSplittedName.join('.');
          const counterToggleValue = dotProp.get(
            this.state.templates,
            counterTogglePath
          );

          // this means both new value of current toggle
          // and existing value of counter toggle are ON
          if (currentToggleValue && counterToggleValue) {
            const newEventToSetCounterToggleToOff = {
              target: {
                name: counterTogglePath,
                checked: false,
                type,
              },
            };

            this.timeRowInputFieldUpdate(newEventToSetCounterToggleToOff);
          }
        }
      }
    );
  };

  onTemplateTypeChange = (event, templateIndex) => {
    const selectedTemplate = this.state.templates[templateIndex];
    const newTemplateType = event.target.value;
    const newOpeningHourTimes = WEEKDAY_ORDER.map(day =>
      this.initializeTimeRow(day, selectedTemplate.id)
    );
    const updatedTemplate = {
      ...selectedTemplate,
      type: newTemplateType,
      opening_hour_times: newOpeningHourTimes,
      state: RESOURCE_STATES.UNSAVED,
      operation_type: this.props.getNewOperationType(selectedTemplate),
    };
    this.setState({
      templates: dotProp.set(
        this.state.templates,
        templateIndex,
        updatedTemplate
      ),
    });
  };

  deleteTemplate = async templateIndex => {
    // if draft -> warn user before discarding and then make an api call
    // if unsaved -> just remove it
    // if published -> set operation_type to 'DELETE' and block editing on it

    let currentTemplate = this.state.templates[templateIndex];
    switch (currentTemplate.state) {
      case 'DRAFT': {
        try {
          const url = '/api/opening-hours/';
          await axiosUI.delete(url, { data: [{ id: currentTemplate.id }] });

          this.props.createNotification({
            message: `Draft is discarded`,
            type: NOTIFICATIONS.TYPE.INFO,
            timeout: 5000,
          });

          this.setState(prevState => {
            return {
              templates: [
                ...prevState.templates.slice(0, templateIndex),
                ...prevState.templates.slice(templateIndex + 1),
              ],
              showDeleteWarningPopup: false,
            };
          });
        } catch (err) {
          console.log(err);
        }
        break;
      }
      case 'UNSAVED': {
        this.setState(prevState => {
          return {
            templates: [
              ...prevState.templates.slice(0, templateIndex),
              ...prevState.templates.slice(templateIndex + 1),
            ],
          };
        });
        break;
      }
      case 'PUBLISHED': {
        currentTemplate = {
          ...currentTemplate,
          operation_type: 'DELETE',
          state: 'UNSAVED',
        };
        this.setState({
          templates: dotProp.set(
            this.state.templates,
            templateIndex,
            currentTemplate
          ),
        });
        break;
      }
      default:
        break;
    }
  };

  submitTemplates = async () => {
    const { templates } = this.state;
    const unsavedTemplates = templates.filter(
      template => template.state === RESOURCE_STATES.UNSAVED
    );

    const eitherTemplateNameOrTimeZoneIsEmpty = unsavedTemplates.some(
      template => !template.name || !template.time_zone
    );

    if (eitherTemplateNameOrTimeZoneIsEmpty) {
      return this.props.createNotification({
        message: `Template name and timezone cannot be empty`,
        type: NOTIFICATIONS.TYPE.ERROR,
        timeout: 50000,
      });
    }

    const transformedOpeningHours = unsavedTemplates.map(template => {
      const updatedOpeningHourTimes = template.opening_hour_times.map(
        timeConfig => {
          return {
            ...timeConfig,
            opening_time: `${timeConfig.openingHour}:${timeConfig.openingMinute}`,
            closing_time: `${timeConfig.closingHour}:${timeConfig.closingMinute}`,
            whole_day: `${timeConfig.wholeDay}`,
            day_off: `${timeConfig.dayOff}`,
          };
        }
      );
      template.opening_hour_times = updatedOpeningHourTimes;
      return template;
    });

    try {
      const url = '/api/opening-hours';
      await axiosUI.post(url, transformedOpeningHours);

      this.props.createNotification({
        message: `Your changes are submitted for review`,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        timeout: 5000,
      });

      this.fetchTemplatesAndSetState();
    } catch (err) {
      console.log(err);

      this.props.createNotification({
        message: `Failed to save the changes`,
        type: NOTIFICATIONS.TYPE.ERROR,
        timeout: 3000,
      });
    }
  };

  navigateToChangesetPage = () => {
    this.props.navigate('changeset');
  };

  render() {
    const hasUnsavedChanges = this.state.templates.some(
      template => template.state === 'UNSAVED'
    );

    return (
      <Fragment>
        <Sidebar highlightNavItemIndex={3} />
        <Topbar className="opening_hours__topbar">
          <span className="font-bold text-lg capitalize">
            <Link className="bb-1-blue no-underline text-blue" to="/channeling">
              Channeling
            </Link>{' '}
            /{' '}
            <Link
              className="bb-1-blue no-underline text-blue"
              to={`/channeling/${this.props.country}/${this.props.language}`}>
              {SALESCHANNEL_NAME_MAP[this.props.country]} -{' '}
              {LOCALE_NAME_MAP[this.props.language]}
            </Link>{' '}
            / Opening Hours
          </span>
          <span className="float-right text-right">
            <Button className="h-8" onClick={this.addNewTemplate}>
              Add new Template
            </Button>
            {this.state.hasDrafts && (
              <PopupWrapper
                className="relative"
                isOpen={this.state.showUnsavedChangesWarningPopup}
                togglePopup={this.toggleUnsavedChangesWarningPopup}>
                <Button
                  type="blue"
                  className="h-8 ml-8"
                  onClick={
                    hasUnsavedChanges
                      ? this.toggleUnsavedChangesWarningPopup
                      : this.navigateToChangesetPage
                  }>
                  Review Pending Draft
                </Button>
                <AnimatePresence>
                  {this.state.showUnsavedChangesWarningPopup && (
                    <motion.div
                      initial={{ opacity: 0, y: '80%', bottom: '-10px' }}
                      animate={{ opacity: 1, y: '100%' }}
                      exit={{ opacity: 0, y: '80%' }}
                      transition={{
                        ease: [0.18, 0.89, 0.39, 1.4],
                        duration: 0.3,
                      }}
                      className="button-popup absolute pin-r px-2 py-4 bg-white shadow-md rounded text-left w-64">
                      <div className="text-center leading-normal">
                        There are some unsaved changes. Are you sure you want to
                        leave this page?
                      </div>
                      <div className="flex justify-around mt-8">
                        <Button
                          className="h-8"
                          onClick={this.toggleUnsavedChangesWarningPopup}>
                          No
                        </Button>
                        <Button
                          type="red-transparent"
                          className="h-8"
                          onClick={this.navigateToChangesetPage}>
                          Yes, leave this page
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </PopupWrapper>
            )}
            <Button
              type="green"
              className="h-8 ml-8"
              onClick={this.submitTemplates}>
              Save as Draft
            </Button>
          </span>
        </Topbar>
        <Main className="opening_hours__main overflow-x-hidden">
          <Loader loading={this.state.loading}>
            <ul className="opening_hour_templates list-reset m-0">
              <AnimatePresence initial={false}>
                {this.state.templates.map((template, templateIndex) => {
                  const isDeleted = template.operation_type === 'DELETE';
                  return (
                    <OpeningHourCard
                      key={template.id}
                      deleteTemplate={this.deleteTemplate}
                      hourRange={this.hourRange}
                      isDeleted={isDeleted}
                      minutesRange={this.minutesRange}
                      onChange={this.onChange}
                      onTemplateTypeChange={this.onTemplateTypeChange}
                      showDeleteWarningPopup={this.state.showDeleteWarningPopup}
                      template={template}
                      templateIndex={templateIndex}
                      toggleDeleteWarningPopup={this.toggleDeleteWarningPopup}
                      onAddDay={this.onAddDay}
                      onRemoveDay={this.onRemoveDay}
                      timeRowInputFieldUpdate={this.timeRowInputFieldUpdate}
                      createNotification={this.props.createNotification}
                    />
                  );
                })}
              </AnimatePresence>
            </ul>
          </Loader>
        </Main>
      </Fragment>
    );
  }
}

export default  withCommonUtils(withGlobalContext(OpeningHoursPerMarket));
